/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment } from 'react'
import { BackTop, Layout } from 'antd'
import { HashRouter as Router, Redirect, Route, Switch } from 'react-router-dom'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import * as _ from 'lodash'

import { RootConsumer } from '@shared/App/Provider'
import { GlobalStore } from '@store/globalStore'
import { ComponentStore } from '@store/componentStore'
import { AuthStore } from '@store/authStore'
import { MatchupStore } from '@store/matchupStore'
import { PortalSocketStore } from '@store/portalSocketStore'
import { LogStore } from '@store/logStore'
import { JoinStore } from '@store/joinStore'
import { UserStore } from '@store/userStore'

import { GoTopIco } from '@shared/Icon/ArrowIcon'
import { BettingHistoryIco, ClubPortalIco, HomeIco, ItemshopIco, MatchupResultIco } from '@shared/Icon/MenuIcon'

import { CompanyRuleWrap, GoTopIcoStyle } from '@styles/base.style'
import { PCLayout, PCScrollStyle } from '@styles/pc.style'

// img
import PCBackground from '@assets/images/bg/pc-background.png'
import PCCompanyRuleImg from '@assets/images/bg/company-rule/company-rule-pc.png'

import Error from '@components/Error'
import {
  appScrollTo,
  getAllHashParameters,
  getAppScrollTop,
  getAttachableHashParameters,
  getHashParameterValue,
  isAccountForTesting,
  isEventPage,
  isNaverAuthResultPage,
  isTestClub,
  isViewHomePage,
  isViewPage,
} from '@utils/utils'
import { HASH_PARAMETER, JOIN_URL, SERVER_STATE, SIDE_MENU } from '@constants/index'
import { PAGE_TYPE, PAGE_WITHOUT_HEADER, SportsFeedMarketProvider } from '@interface/common'
import BetSlip from '@views/Matchup/BetSlip'
import MatchUpDetail from '@views/Matchup/MatchUpDetail'
import Guide from '@views/Guide'
import ItemShop from '@views/ItemShop'
import BettingHistory from '@views/BettingHistory'
import Club from '@views/Club'
import Login from '@views/Login'
import ServerCheck from '@views/ServerCheck'
import NoAccess from '@views/NoAccess'
import LiveCount from '@views/Matchup/BetSlip/LiveCount'
// import Splash from '@views/Splash'
import GeneralPopup from '@views/Popup/General'
import MissionCompletePopup from '@views/Popup/MissionComplete'
import Chat from '@views/Popup/Chat'
import { ChattingStore } from '@store/chattingStore'
import EventPage from '@views/EventPage'
// import Dialog from '@views/Popup/Dialog'
// import { isShowAttendancePopup } from '@views/Popup/Event/Attendance'
import NaverAuthResultPage from '@views/NaverAuthResult'
import MatchTab from '@views/Matchup/MatchField/MatchTab'
import AdvNewsList from '@views/Home/AdvNews/AdvNewsList'
import { FEED_SUPPLIER, FROM_VIEW_TYPE, GlobalStoreKey } from '@v2/constants/common'
import { CompanyPickStore } from '@store/companyPickStore'
import TopNoticeBar from '@views/Notice/TopNoticeBar'
import PopupLayout from '@views/Popup'
import Sider from './Sider'
import Header from './Header'
import { asynchronousComponents, filterMenus, RouteMenu } from './menu'
import UserPickPage from '@views/Home/UserPick/UserPickPage'
import StorageUtils from '@utils/storage-utils'
import { UserPickStore } from '@store/userPickStore'
import { ClubStore } from '@store/clubStore'
import DiaBubbleImgPc from '@assets/images/icon/etc_icon/dia-bubble-alert-pc.png'
import DiaBubbleSeasonImgPc from '@assets/images/bg/season-bg/season-dia-bubble-alert-pc.png'
import TodayPickManagerList from 'containers/views/Home/TodayPick/CompanyPickManagerList'
import TodayPickDetail from '@views/Home/TodayPick/CompanyPickDetail'
import { PopularSearchTerm } from 'components/PopularSearchTerm'
import { BettingHistoryStore } from '@store/bettingHistoryStore'
import MatchupBannerPopup from '@views/Popup/MatchupBanner'

interface PrimaryLayoutProps {
  authStore: AuthStore
  componentStore: ComponentStore
  globalStore: GlobalStore
  routerStore: RouterStore
  matchupStore: MatchupStore
  portalSocketStore: PortalSocketStore
  logStore: LogStore
  chattingStore: ChattingStore
  joinStore: JoinStore
  userStore: UserStore
  companyPickStore: CompanyPickStore
  userPickStore: UserPickStore
  clubStore: ClubStore
  bettingHistoryStore: BettingHistoryStore
}

interface PrimaryLayoutState {
  isLoaded: boolean;
}

let intervalCheckAppVersion = null
let intervalBestPickBannerShowCheck = null

@observer
class PrimaryLayout extends React.PureComponent<PrimaryLayoutProps, PrimaryLayoutState> {
  private readonly authStore: AuthStore

  private readonly componentStore: ComponentStore

  private readonly globalStore: GlobalStore

  private readonly routerStore: RouterStore

  private readonly matchupStore: MatchupStore

  private readonly portalSocketStore: PortalSocketStore

  private readonly logStore: LogStore

  private readonly chattingStore: ChattingStore

  private readonly joinStore: JoinStore

  private readonly userStore: UserStore

  private readonly companyPickStore: CompanyPickStore

  private readonly userPickStore: UserPickStore

  private readonly clubStore: ClubStore

  private readonly bettingHistoryStore: BettingHistoryStore

  constructor(props: PrimaryLayoutProps) {
    super(props)
    console.timeEnd('Mobx store initializing - PrimaryLayout constructor')
    const {
      authStore,
      componentStore,
      globalStore,
      routerStore,
      matchupStore,
      portalSocketStore,
      logStore,
      chattingStore,
      joinStore,
      userStore,
      companyPickStore,
      userPickStore,
      clubStore,
      bettingHistoryStore,
    } = this.props
    this.state = {
      isLoaded: false,
    }
    this.authStore = authStore
    this.componentStore = componentStore
    this.globalStore = globalStore
    this.routerStore = routerStore
    this.matchupStore = matchupStore
    this.portalSocketStore = portalSocketStore
    this.logStore = logStore
    this.chattingStore = chattingStore
    this.joinStore = joinStore
    this.userStore = userStore
    this.companyPickStore = companyPickStore
    this.userPickStore = userPickStore
    this.clubStore = clubStore
    this.bettingHistoryStore = bettingHistoryStore
  }

  updateActiveBtns = () => {
    this.globalStore.getMenuAvailableList()
  }

  onMountEvent = async () => {
    console.time('PrimaryLayout on mount event')

    try {
      let marketingId = ''
      const firstStatistics = getHashParameterValue(HASH_PARAMETER.MARKETING_FIRST_STATISTICS.KEY)
      if (!this.authStore.isSignedIn && firstStatistics) {
        marketingId = getHashParameterValue(HASH_PARAMETER.MARKETING_ID.KEY)
        this.logStore.fetchReferrer({ referrer: document.referrer, marketingId }).catch(err => console.error(err))
      } else {
        this.logStore.fetchReferrer({ referrer: document.referrer }).catch(err => console.error(err))
      }
    } catch (err) {
      console.dir(err)
    }

    await this.globalStore.getGlobalSettings()
    const globalValue = this.globalStore.findGlobalSetting(GlobalStoreKey.SPORTS_FEED_MARKET_PROVIDER)
    if (globalValue && !_.isNil(globalValue.value)) {
      const marketProviderData = JSON.parse(globalValue.value) as SportsFeedMarketProvider
      const feedSupplier = marketProviderData.pre > 0 ? FEED_SUPPLIER.LSPORTS : FEED_SUPPLIER.BET_RADAR
      this.matchupStore.setFeedSupplier(feedSupplier)
      this.matchupStore.setMarketProviderData(marketProviderData)
    }
    if (!isMobile) {
      this.updateActiveBtns()
    }

    if (isEventPage() || isNaverAuthResultPage()) {
      this.setState({ isLoaded: true })
      return
    }
    let clubId = StorageUtils.getLastClubId()
    if (isTestClub(clubId)) {
      const globalValue = this.globalStore.findGlobalSetting(GlobalStoreKey.REPRESENTATIVE_CLUB)?.value
      clubId = globalValue || process.env.DEFAULT_CLUB_ID
    }
    this.authStore.fetchUser(false, clubId).then(() => {
      this.matchupStore.fetchFavoriteTotalPreMatchCount().catch(err => console.error(err))

      if (this.authStore.isSignedIn) {
        this.clubStore.fetchSimpleClubs().catch(console.error)
        this.userPickStore.fetchLastBestUserPickId().catch(console.error)
        this.userStore.getUserGameOption().catch(console.error)
        this.componentStore.fetchMatchupNotices().catch(console.error)
      }
    }).catch(err => {
      console.error(err)
    })
    this.setState({ isLoaded: true })

    this.componentStore.fetchExtraOddsItemDetails().catch(console.error)
    this.userStore.fetchUserTitleDefs().catch(console.error)

    const android = window.android
    if (android) {
      android.jnUpdateMobileAppVersion()
    }

    // 전환측정 라이브러리 등에서 postMessage 를 사용하며 의도하지 않은 수신 후 리스너가 제거될 수 있으므로 once 옵션을 사용하지 않음
    window.addEventListener('message', this.adultAuthenticationListener)

    const pathName = this.routerStore.location.pathname
    if (!pathName.startsWith('/agree')
      && !pathName.startsWith('/serverCheck')
      && !this.authStore.isForceShowLoginPopup) {
      this.globalStore.setVisiblePartnershipBannerPopup(true)
    }

    console.timeEnd('PrimaryLayout on mount event')
    console.timeEnd('Mobx store initializing - PrimaryLayout render start')
    console.timeEnd('App index start - PrimaryLayout render start')
  }

  adultAuthenticationListener = e => {
    const eventOrigin = e.origin

    const baseUrl = process.env.BACKEND_BASE_URL
    const port = process.env.BACKEND_PORT ? `:${process.env.BACKEND_PORT}` : ''
    const origin = baseUrl + port

    if (eventOrigin === origin && e.data?.message === 'authResult' && this.authStore.adultAuthenticationResultHandler) {
      this.authStore.adultAuthenticationResultHandler(e.data)
    }
  }

  componentWillUnmount() {
    window.addEventListener('message', this.adultAuthenticationListener)

    clearInterval(intervalCheckAppVersion)
    clearInterval(intervalBestPickBannerShowCheck)
  }

  componentDidMount = async () => {
    clearInterval(intervalCheckAppVersion)
    clearInterval(intervalBestPickBannerShowCheck)
    this.componentStore.checkAppVersion().catch(console.error)
    intervalCheckAppVersion = setInterval(this.componentStore.checkAppVersion, 30000)
    intervalBestPickBannerShowCheck = setInterval(() => this.globalStore.checkBestPickShowSchedule(), 10000)

    if (!_.isEmpty(_.trim(getHashParameterValue(HASH_PARAMETER.APPLE_AUTH.KEY)))) {
      const realAppleLoginButton = document.getElementById('appleid-signin')
      realAppleLoginButton.click()
      return
    }

    // if (!isEventPage()) {
    //   this.globalStore.toggleSplash(true)
    //   setTimeout(() => {
    //     if (this.globalStore.isSplash()) {
    //       this.globalStore.toggleSplash(false)
    //     }
    //   }, 4000)
    // }
    this.onMountEvent()

    const screenHeight = StorageUtils.getScreenHeight()
    if (!screenHeight) {
      StorageUtils.saveScreenHeight(window.innerHeight)
    }

    this.routerStore.history.listen(location => {
      const { isVisibleChattingModal } = this.chattingStore
      if (isMobile && isVisibleChattingModal) {
        this.chattingStore.changeChattingModalVisible(false)
      }
    })
  }

  getUserLastClubId = (): string => {
    if (!_.isEmpty(this.authStore.userInfo)) {
      return this.authStore.userInfo.clubId
    }
    return null
  }

  getRedirectUrl() {
    if (!this.authStore.isSignedIn) {
      const hashParameters = getAllHashParameters(true)
      const globalValue = this.globalStore.findGlobalSetting(GlobalStoreKey.DEFAULT_GUEST_CLUB)?.value
      const guestClubId = globalValue ?? process.env.DEFAULT_GUEST_CLUB_ID
      return `/matchup/cross/${guestClubId}${hashParameters}`
    }
    const representativeClubId = this.globalStore.findGlobalSetting(GlobalStoreKey.REPRESENTATIVE_CLUB)?.value ?? null
    let clubId = StorageUtils.getLastClubId()
      || this.getUserLastClubId() || representativeClubId || process.env.DEFAULT_CLUB_ID
    const userInfo = this.authStore.userInfo
    if (!_.isEmpty(userInfo) && userInfo.isSpecificClubMember && userInfo.clubId !== clubId) {
      clubId = userInfo.clubId
    }
    return `/matchup/cross/${clubId}`
  }

  // 충전 바로가기
  // onClickChargeLinkAnchor = () => {
  //   let url
  //   if (this.authStore.userInfo && this.authStore.authInfo) {
  //     const authKey = this.authStore.authInfo.authKey
  //     url = `${process.env.HOMEPAGE_PC}/#/auth_login/${authKey}/payment`
  //   } else {
  //     url = `${process.env.HOMEPAGE_PC}/#/payment`
  //   }
  //   this.globalStore.toMoveWebHomePage(url)
  // }

  // 홈페이지 바로가기
  onClickHomeLinkAnchor = () => {
    let url
    if (this.authStore.userInfo && this.authStore.authInfo) {
      const authKey = this.authStore.authInfo.authKey
      url = `${process.env.HOMEPAGE_PC}/#/auth_login/${authKey}/main`
    } else {
      const hashParameters = getAttachableHashParameters(true)
      url = `${process.env.HOMEPAGE_PC}/#/main${hashParameters}`
    }
    this.globalStore.toMoveWebHomePage(url)
    return false
  }

  // 공식카페 바로가기
  onClickCafeLinkAnchor = () => {
    this.globalStore.toMoveWebHomePage('https://cafe.naver.com/adventureronline')
  }

  onClickPcSideMenu = (menuType: SIDE_MENU) => {
    if (!this.authStore.isSignedIn) {
      this.authStore.showLoginPopup(<Login/>)
      return false
    }
    switch (menuType) {
      case SIDE_MENU.CLUB: {
        this.logStore.writeButtonLog({ pageId: 'i010', buttonId: '003' }).catch(console.error)
        const title = this.authStore.isSpecificClubMember ? '클럽정보' : '클럽이동'
        this.globalStore.addGeneralPopupProperties(<Club/>, title, {
          closeCallback: () => {
            this.logStore.writeButtonLog({ pageId: 'd040', buttonId: '001' }).catch(console.error)
          },
        })
      }
        break
      case SIDE_MENU.ITEM_SHOP:
        this.logStore.writeButtonLog({ pageId: 'i010', buttonId: '006' }).catch(console.error)
        this.globalStore.addGeneralPopupProperties(<ItemShop/>, '상점', {
          closeCallback: () => {
            this.logStore.writeButtonLog({ pageId: 'd020', buttonId: '001' }).catch(console.error)
          },
        })
        break
      case SIDE_MENU.MATCHUP_RESULT:
        this.logStore.writeButtonLog({ pageId: 'i010', buttonId: '005' }).catch(console.error)
        this.matchupStore.setMatchTabInitializeMajorUniqueId(undefined)
        this.matchupStore.setMatchTabInitializeSportId(undefined)
        this.globalStore.setVisibleUserPick(false)
        this.globalStore.setVisibleAdvNews(false)
        this.globalStore.setVisibleGuide(false)
        this.globalStore.setVisibleTodayPick(false)
        this.routerStore.push(`/${menuType}`)
        break
      case SIDE_MENU.BETTING_HISTORY:
        this.logStore.writeButtonLog({ pageId: 'i010', buttonId: '004' }).catch(console.error)
        this.bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.BETTING_HISTORY)
        this.bettingHistoryStore.setBettingHistoryInitProps({
          onlyMajorClub: false,
          fromViewType: FROM_VIEW_TYPE.BETTING_HISTORY,
        })
        this.globalStore.addGeneralPopupProperties(<BettingHistory/>, '베팅 내역', {
          isScroll: false,
          closeCallback: () => {
            this.logStore.writeButtonLog({ pageId: 'c010', buttonId: '002' }).catch(console.error)
          },
        })
        break
    }
    return false
  }

  isRedirectRoute = (menuPath: string): boolean => {
    const isDirectAccessPage = _.find(JOIN_URL, url => _.toLower(`/${url}`) === _.toLower(menuPath))
    if (isDirectAccessPage) {
      return false
    }

    if (_.isEmpty(this.authStore.userInfo)) {
      return true
    }

    if (this.authStore.isSpecificClubMember && menuPath === '/pick') {
      return true
    }

    return false
  }

  renderLayoutContents = () => {
    const userId = this.authStore.userInfo?.id || ''
    const filteredMenu = filterMenus(
      true,
      this.authStore.isSpecificClubMember,
      this.globalStore.getMissionEnabled(),
      isAccountForTesting(userId),
      this.globalStore.getFriendInvitationEnabled(),
      this.componentStore.isPossibleInvitor,
    )

    let layoutContentStyle
    if (!isMobile) {
      layoutContentStyle = { height: '100%' }
    }
    return (
      <Layout.Content
        key="PrimaryLayout:LayoutContent"
        css={{
          '& > *': {
            display: 'flex',
            width: '100%',
            flex: 1,
          },
        }}
        style={layoutContentStyle}
        id="main_container_id"
      >
        <Router>
          <Switch>
            {filteredMenu.map((m: RouteMenu) => {
              if (!m.path || !m.component) {
                return null
              }

              return (
                <Route
                  key={m.id}
                  exact={m.exact}
                  path={m.path}
                  component={m.component ? asynchronousComponents[m.component] : null}
                >
                  {(this.isRedirectRoute(m.path) && (<Redirect to={this.getRedirectUrl()}/>))}
                </Route>
              )
            })}
            <Route path="/empty" component={asynchronousComponents['empty']}/>
            <Route render={() => <Error/>}/>
          </Switch>
        </Router>
      </Layout.Content>
    )
  }

  renderSubLayoutContents = () => {
    let result = (<MatchUpDetail/>)
    if (this.globalStore.visibleGuide) {
      result = (<Guide/>)
    } else if (this.globalStore.visibleTodayPick) {
      result = (<TodayPickDetail/>)
    } else if (this.globalStore.visibleAdvNews) {
      result = (<AdvNewsList/>)
    } else if (this.globalStore.visibleUserPick) {
      result = (<UserPickPage/>)
    } else if (this.globalStore.visibleTodayPickList) {
      result = (<TodayPickManagerList/>)
    }
    return result
  }

  getIsMember() {
    const { club = {} } = this.matchupStore
    const { isMember } = club

    return isMember ?? false
  }

  backTopScrollEventHandler = () => {
    const position = getAppScrollTop()
    if (position <= 0) {
      this.backTopScrollEndEventHandler()
    }
  }

  backTopScrollEndEventHandler = () => {
    window.removeEventListener('scroll', this.backTopScrollEventHandler, true)
    setTimeout(() => {
      appScrollTo(1)
    }, 20)
  }

  renderContents = () => {
    let header
    if (isViewHomePage(PAGE_WITHOUT_HEADER.HOME)
      || isViewHomePage(PAGE_WITHOUT_HEADER.TODAY_PICK)
      || isViewHomePage(PAGE_WITHOUT_HEADER.USER_PICK)
      || isViewHomePage(PAGE_WITHOUT_HEADER.ADV_NEWS)) {
      header = <Fragment/>
    } else {
      header = <Header key="PrimaryLayout:Header"/>
    }
    const isMember = this.getIsMember()
    if (isMobile) {
      return (
        <Fragment>
          {!isEventPage() && !isNaverAuthResultPage() && !this.globalStore.visibleNoAccess && (
            <Fragment>
              <Sider key="PrimaryLayout:Sider"/>
              {header}
              <Layout key="PrimaryLayout:Layout" className="m-layout">
                {this.authStore.isSignedIn && isViewPage(PAGE_TYPE.MATCHUP) && <TopNoticeBar/>}
                {isViewPage(PAGE_TYPE.MATCHUP) && <PopularSearchTerm/>}
                {isViewPage(PAGE_TYPE.MATCHUP) && <MatchTab key="Matchup:MatchTab" pageType={PAGE_TYPE.MATCHUP}/>}
                {this.renderLayoutContents()}
              </Layout>
              {isMember &&
                <BackTop
                  key="PrimaryLayout:BackTop"
                  onClick={e => {
                    window.addEventListener('scroll', this.backTopScrollEventHandler, true)
                  }}
                >
                  <GoTopIcoStyle>
                    <GoTopIco/>
                  </GoTopIcoStyle>
                </BackTop>
              }
              <PopupLayout key="PrimaryLayout:ComponentPopup"/>
              {/*<Dialog key="PrimaryLayout:Dialog"/>*/}
              <MissionCompletePopup/>
            </Fragment>
          )}
          {this.globalStore.serverState === SERVER_STATE.ON && this.globalStore.visibleNoAccess && (
            <Fragment>
              <Header key="PrimaryLayout:Header"/>
              <NoAccess/>
            </Fragment>
          )}
          {isEventPage() && (<EventPage/>)}
          {isNaverAuthResultPage() && (<NaverAuthResultPage/>)}
          {/*<NavBar />*/}
        </Fragment>
      )
    }

    const onCloseFilterPopup = () => {
      this.globalStore.setVisibleMatchFilterPopup(false)
    }
    const { isChatEnabled } = this.chattingStore

    let giftBoxAlert
    const giftbox = this.componentStore.giftbox
    const homeMenu =  window.location.hash === '#/home'

    if (this.authStore.isSignedIn && giftbox?.openEnabled && !homeMenu) {
      giftBoxAlert = <img src={this.globalStore.isGiftBoxSeasonEffect() ? DiaBubbleSeasonImgPc : DiaBubbleImgPc}
                          alt="무료충전 가능 알림" className="gift-box-alert-bubble"/>
    } else {
      giftBoxAlert = <Fragment/>
    }

    //this.authStore.isSignedIn && isViewPage(PAGE_TYPE.MATCHUP)
    return (
      <div css={PCLayout} className={isEventPage() || isNaverAuthResultPage() ? 'event-page-layout' : ''}>
        {this.globalStore.serverState === SERVER_STATE.OFF && (
          <Fragment>
            <Header key="PrimaryLayout:Header"/>
            <div className={this.componentStore.maintenanceBanner ? 'server-check-wrap is-banner' : 'server-check-wrap'}>
              <ServerCheck/>
            </div>
            {this.globalStore.generalPopupQueue.map(popup => {
              return <GeneralPopup key={popup.popupId} {...popup}/>
            })}
            {/*<Dialog key="PrimaryLayout:Dialog"/>*/}
            <MatchupBannerPopup/>
          </Fragment>
        )}
        {this.globalStore.serverState === SERVER_STATE.ON && this.globalStore.visibleNoAccess && (
          <Fragment>
            <Header key="PrimaryLayout:Header"/>
            <NoAccess/>
          </Fragment>
        )}
        {this.globalStore.serverState === SERVER_STATE.ON
          && !isEventPage() && !isNaverAuthResultPage() && !this.globalStore.visibleNoAccess && (
            <Fragment>
              <img src={PCBackground} alt="축구 경기장" className="pc-bg"/>
              <Sider key="PrimaryLayout:Sider"/>
              <Header key="PrimaryLayout:Header"/>
              <LiveCount key="Matchup:LiveCount" matchupStore={this.matchupStore} globalStore={this.globalStore}/>

              {/* ************ only pc ************ */}
              <div className="pc-menu">
                <ul className={`pc-menu--list ${this.joinStore.isJoinUrl ? 'adv-hidden-fill-space' : ''}`}>
                  <li className="pc-menu--list--item">
                    <button
                      type="button"
                      onClick={() => {
                        this.logStore.writeButtonLog({ pageId: 'i010', buttonId: '002' }).catch(console.error)
                        this.globalStore.setVisibleUserPick(false)
                        this.globalStore.setVisibleAdvNews(false)
                        this.globalStore.setVisibleGuide(false)
                        this.globalStore.setVisibleTodayPick(false)
                        this.routerStore.push('/home')
                        onCloseFilterPopup()
                      }}
                      className="menu betting-history">
                      <HomeIco/>
                      <p className="menu--txt">홈</p>
                      <span className="menu--arrow"/>
                      {giftBoxAlert}
                    </button>
                  </li>
                  {this.globalStore.menuAvailableList.indexOf(SIDE_MENU.CLUB) >= 0 && (
                    <li className="pc-menu--list--item">
                      <button
                        type="button"
                        onClick={() => this.onClickPcSideMenu(SIDE_MENU.CLUB)}
                        className="menu club-portal">
                        <ClubPortalIco/>
                        <p className="menu--txt">{!this.authStore.isSpecificClubMember ? '클럽이동' : '클럽정보'}</p>
                        <span className="menu--arrow"/>
                      </button>
                    </li>
                  )}
                  {this.globalStore.menuAvailableList.indexOf(SIDE_MENU.BETTING_HISTORY) >= 0 && (
                    <li className="pc-menu--list--item">
                      <button
                        type="button"
                        onClick={() => this.onClickPcSideMenu(SIDE_MENU.BETTING_HISTORY)}
                        className="menu betting-history">
                        <BettingHistoryIco/>
                        <p className="menu--txt">베팅내역</p>
                        <span className="menu--arrow"/>
                      </button>
                    </li>
                  )}
                  {this.globalStore.menuAvailableList.indexOf(SIDE_MENU.MATCHUP_RESULT) >= 0 && (
                    <li className="pc-menu--list--item">
                      <button
                        type="button"
                        onClick={() => {
                          this.onClickPcSideMenu(SIDE_MENU.MATCHUP_RESULT)
                          onCloseFilterPopup()
                        }}

                        className="menu matchupResult">
                        <MatchupResultIco/>
                        <p className="menu--txt">경기결과</p>
                        <span className="menu--arrow"/>
                      </button>
                    </li>
                  )}
                  {this.globalStore.menuAvailableList.indexOf(SIDE_MENU.ITEM_SHOP) >= 0 && (
                    <li className="pc-menu--list--item">
                      <button
                        type="button"
                        onClick={() => this.onClickPcSideMenu(SIDE_MENU.ITEM_SHOP)}
                        className="menu itemshop">
                        <ItemshopIco/>
                        <p className="menu--txt">상점</p>
                        <span className="menu--arrow"/>
                      </button>
                    </li>
                  )}
                  {/*{this.authStore.isSignedIn && (*/}
                  {/*  <li className="pc-menu--list--item">*/}
                  {/*    <a href="/#" className="menu link" onClick={this.onClickChargeLinkAnchor}>*/}
                  {/*      <MoneyBagIco/>*/}
                  {/*      <p className="menu--txt">충전</p>*/}
                  {/*      <span className="menu--arrow"/>*/}
                  {/*    </a>*/}
                  {/*  </li>*/}
                  {/*)}*/}

                  {/*<li className="pc-menu--list--item line home-ico">*/}
                  {/*  <a href={window.location.href} className="menu link" onClick={this.onClickHomeLinkAnchor}><HomeIco/>홈페이지</a>*/}
                  {/*</li>*/}
                  {/*{!this.authStore.isSpecificClubMember && this.globalStore.menuAvailableList.indexOf(SIDE_MENU.NAVER_CAFE) >= 0 && (*/}
                  {/*  <li className="pc-menu--list--item">*/}
                  {/*    <a href="/#" className="menu link" onClick={this.onClickCafeLinkAnchor}><CafeIco/>공식카페</a>*/}
                  {/*  </li>*/}
                  {/*)}*/}
                </ul>

                <ul className={`pc-menu--list ${!this.joinStore.isJoinUrl ? 'adv-hidden-fill-space' : ''}`}>
                  <li className="pc-menu--list--item"/>
                </ul>

                {isChatEnabled
                  && !this.joinStore.isJoinUrl
                  && !this.authStore.isSpecificClubMember
                  && <Chat key="PrimaryLayout:Chat" pcChatWrapClass="pc-chat open"/>}
              </div>

              <Layout key="PrimaryLayout:Layout" className="pc-layout">
                <div className="main-page">
                  {this.authStore.isSignedIn && isViewPage(PAGE_TYPE.MATCHUP) && <TopNoticeBar/>}
                  {this.renderLayoutContents()}
                </div>
                {/* ************ only pc ************ */}
                <div className="sub-page">
                  {this.renderSubLayoutContents()}
                </div>
              </Layout>

              {/* ************ only pc ************ */}
              <div id="pcBetslip" className="pc-betslip">
                <BetSlip/>
                {/* <BetSlip key="Matchup:BetSlip" /> */}
                <CompanyRuleWrap className="pc-company-rule">
                  <img src={PCCompanyRuleImg} alt="등급분류 이미지" style={{ width: '100%' }}/>
                </CompanyRuleWrap>
              </div>


              <BackTop key="PrimaryLayout:BackTop">
                <GoTopIcoStyle>
                  <GoTopIco/>
                </GoTopIcoStyle>
              </BackTop>
              <PopupLayout key="PrimaryLayout:ComponentPopup"/>
              {/*<Dialog key="PrimaryLayout:Dialog"/>*/}
              <MissionCompletePopup/>
            </Fragment>
          )}
        {this.globalStore.serverState === SERVER_STATE.ON && isEventPage() && (
          <Fragment>
            <EventPage/>
          </Fragment>
        )}
        {this.globalStore.serverState === SERVER_STATE.ON && isNaverAuthResultPage() && (
          <Fragment>
            <NaverAuthResultPage/>
          </Fragment>
        )}
      </div>
    )
  }

  render() {
    const { isLoaded } = this.state
    // if (this.globalStore.splash) {
    //   return <Splash/>
    // }

    if (isLoaded) {
      return (
        <div css={!isMobile && PCScrollStyle}>
          {this.renderContents()}
        </div>
      )
    }

    return null
  }
}

export default function Wrapper() {
  return (
    <RootConsumer>
      {({
          companyPickStore,
          componentStore,
          globalStore,
          authStore,
          routerStore,
          matchupStore,
          portalSocketStore,
          logStore,
          chattingStore,
          joinStore,
          userStore,
          userPickStore,
          clubStore,
          bettingHistoryStore,
        }) => (
        <PrimaryLayout
          key="PrimaryLayout"
          globalStore={globalStore}
          authStore={authStore}
          componentStore={componentStore}
          routerStore={routerStore}
          matchupStore={matchupStore}
          portalSocketStore={portalSocketStore}
          logStore={logStore}
          chattingStore={chattingStore}
          joinStore={joinStore}
          userStore={userStore}
          companyPickStore={companyPickStore}
          userPickStore={userPickStore}
          clubStore={clubStore}
          bettingHistoryStore={bettingHistoryStore}
        />
      )}
    </RootConsumer>
  )
}
